import { commafy, uiLabel } from 'Utils';
import { getDominantEntityIncidence, overviewTableKey } from 'DataDiscovery/discoverApi';
import { SEVERITY_RAMP } from 'Utils/styleTokens';


const mostImportantTypes = [ "NAME","ADDRESS","ADDRESS-STREET","ADDRESS-CITY","ADDRESS-STATE","ZIP_CODE","COUNTRY"
,"PHONE","EMAIL", "SSN", "CREDIT_CARD", "CREDIT_CARD_NUMBER"];


// unranked
// "INTERNATIONAL_BANK_ACCOUNT_NUMBER", "PIN",  "BANK_ROUTING", "SWIFT_CODE", 
// "US_INDIVIDUAL_TAX_IDENTIFICATION_NUMBER", "IP_ADDRESS", "MAC_ADDRESS","LICENSE_PLATE","DRIVER_LICENSE",
// "URL", "VIN", "USERNAME",   "US_INDIVIDUAL_TAX_IDENTIFICATION_NUMBER", "PASSPORT_NUMBER",  "CA_SOCIAL_INSURANCE_NUMBER", "IN_AADHAAR", "IN_NREGA", "IN_PERMANENT_ACCOUNT_NUMBER", 
// "IN_VOTER_NUMBER", "UK_UNIQUE_TAXPAYER_REFERENCE_NUMBER","MEDICARE_BENEFICIARY_IDENTIFIER", "CA_HEALTH_NUMBER", "UK_NATIONAL_HEALTH_SERVICE_NUMBER", 
// "UK_NATIONAL_INSURANCE_NUMBER","AGE"]

const leastImportantTypes = ["DATE_TIME", "LOW_INCIDENCE_FINDINGS"];

// Non-virtualized scatter safety cap (PeopleSoft-80K readiness, chunk C5). MarkSeries
// renders every row in the DOM with no windowing, and heightValue grows 40px/row — an
// old monolithic run (pre table-index mode) or any non-flagged deployment can still
// hand this function tens of thousands of tables. Past this many distinct tables-with-
// findings, keep only the top ones by total findings and fold the rest into one
// non-clickable summary row (see isAggregateRowLabel / EntityMarkSeries' tick renderer).
const SCATTER_TABLE_CAP = 200;

/** Aggregate overflow-row label built by the SCATTER_TABLE_CAP truncation below. */
export function buildAggregateRowLabel(overflowCount, itemLabel = 'tables') {
  return `(+${commafy(overflowCount)} more ${itemLabel})`;
}

/** True for a y-axis tick that is the synthetic overflow row, not a real table name. */
export function isAggregateRowLabel(name) {
  return typeof name === 'string' && /^\(\+[\d,]+ more (?:tables|files|collections)\)$/.test(name);
}

/**
 * Default (classic DB/table) view: one mark per (table, entity type) actually present
 * anywhere in that table. Every entity found in every field is summed into the pair's
 * total, not just each field's single dominant entity — a free-text/structured field with
 * several distinct kinds of PII contributes all of them (the point this fixes is that the
 * old per-field-dominant selection silently dropped every non-dominant entity from this
 * chart). X = entity type; size = combined count across every contributing field. The
 * low-incidence split still applies, recomputed off the summed total.
 *
 * options.allEntities (folder/file view): one mark per (table, entity) for EVERY entity
 * found, keyed by MAX count across fields rather than a sum. Document rows expose pages as
 * fields, so dominant-only hid every co-located entity after the page's biggest — a page
 * holding EMAIL and PERSON showed only one. Entities can't overlap on the plot (each has
 * its own x column), so showing all is safe. The low-incidence split doesn't apply here.
 */
const getScatterVisData = (entity, overviewTables, lowConfidence, entityTablesForTotals, options = {}) => {
  const { sortBy = 'count', sortDir = 'asc', filterTerm = '', allEntities = false, dataLabel = 'tables' } = options;
  const tables = Array.isArray(overviewTables) ? overviewTables : [];
  // Every per-table map below is keyed by overviewTableKey (`schema.table` when the
  // item carries a schema, bare name otherwise). Keying by bare name collapsed
  // same-named tables across schemas into ONE row that summed their findings — a
  // 3-tenant DB with tenant_a/b/c.patients showed one `patients` row at 3× the count.
  const totalByTable = {};
  tables.forEach((t) => {
    let tot = 0;
    (t.fields || []).forEach((f) => {
      Object.values(f.findings || {}).forEach((c) => { tot += c; });
    });
    totalByTable[overviewTableKey(t)] = tot;
  });

  try {
    const rawPoints = [];                 // allEntities (folder/file) mode
    const sumByTableEntity = new Map();    // default mode: key `${table}|${entityType}`
    const rowsSampledByTable = new Map();

    for (const table of tables) {
      const tableName = overviewTableKey(table);
      const rowsSampled = table.rows_sampled || 1;
      rowsSampledByTable.set(tableName, rowsSampled);
      for (const field of table.fields || []) {
        const findings = field.findings || {};

        if (allEntities) {
          const entities = { ...findings };
          if (Object.keys(entities).length === 0) continue;
          if (entity !== 'ALL' && !(entities[entity] > 0)) continue;
          Object.entries(entities).forEach(([ent, cnt]) => {
            if (entity !== 'ALL' && ent !== entity) return;
            if (!(cnt > 0)) return;
            rawPoints.push({
              yTable: tableName,
              fieldName: field.name,
              entityValue: ent,
              dominantEntity: ent,
              count: cnt,
              table: tableName,
              opacity: 0.75
            });
          });
          continue;
        }

        // Default mode: sum every entity in every field's findings into a running
        // (table, entityType) total. Zero-count entries (metadata-only: entity
        // identified from the column name alone, no sampled instances) are summed in
        // too, not dropped — so getDominantEntityIncidence's metadata-only placeholder
        // logic (below) still applies, matching the old per-field behavior for that case.
        for (const [entityType, count] of Object.entries(findings)) {
          if (entity !== 'ALL' && entityType !== entity) continue;

          const k = `${tableName}|${entityType}`;
          if (!sumByTableEntity.has(k)) {
            sumByTableEntity.set(k, { tableName, entityType, count: 0, fieldNames: [] });
          }
          const agg = sumByTableEntity.get(k);
          agg.count += count;
          agg.fieldNames.push(field.name);
        }
      }
    }

    let mergedPoints;
    if (allEntities) {
      const combinedByTableEntity = new Map();
      rawPoints.forEach((p) => {
        const k = `${p.table}|${p.dominantEntity}`;
        if (!combinedByTableEntity.has(k)) {
          combinedByTableEntity.set(k, {
            yTable: p.yTable,
            table: p.table,
            dominantEntity: p.dominantEntity,
            entityValue: p.dominantEntity,
            fieldNames: [],
            count: 0,
            opacity: p.opacity
          });
        }
        const agg = combinedByTableEntity.get(k);
        agg.count = Math.max(agg.count, p.count);
        agg.fieldNames.push(p.fieldName);
      });

      mergedPoints = Array.from(combinedByTableEntity.values()).map((agg) => {
        // Collapse duplicate field names so the scatter agrees with the table detail
        // view, which keys fields by name (getTablePanelData). Without this, N columns
        // that share a name render as "N fields (SSN, SSN, SSN)" here while the table
        // view shows one — same overview payload, different presentation.
        agg.fieldNames = Array.from(new Set(agg.fieldNames));
        agg.fieldNames.sort((a, b) => a.localeCompare(b));
        const size = agg.count > 15 ? agg.count : 15;
        return {
          ...agg,
          size
        };
      });
    } else {
      mergedPoints = [];
      for (const agg of sumByTableEntity.values()) {
        const rowsSampled = rowsSampledByTable.get(agg.tableName) || 1;
        // Low-incidence classification is recomputed off the summed total (not kept
        // per-field), since a point can now represent contributions from several
        // fields — a degenerate single-key call, legal since agg.entityType is always
        // exactly one type by construction of the Map key above.
        const {
          dominantCount,
          isLowIncidence
        } = getDominantEntityIncidence({ [agg.entityType]: agg.count }, rowsSampled);

        if (lowConfidence && !isLowIncidence) continue;
        if (!lowConfidence && isLowIncidence) continue;

        // Collapse duplicate field names (see allEntities branch note) so the scatter
        // agrees with the table detail view.
        const fieldNames = Array.from(new Set(agg.fieldNames)).sort((a, b) => a.localeCompare(b));
        const size = dominantCount > 15 ? dominantCount : 15;

        mergedPoints.push({
          yTable: agg.tableName,
          table: agg.tableName,
          dominantEntity: agg.entityType,
          entityValue: agg.entityType,
          fieldNames,
          count: dominantCount,
          opacity: lowConfidence ? 0.25 : 0.75,
          size
        });
      }
    }

    // Non-virtualized scatter cap: past SCATTER_TABLE_CAP distinct tables-with-findings,
    // keep only the top ones by total findings (independent of the current entity/
    // low-confidence filter — totalByTable sums every entity across every field) and
    // fold the rest into one aggregate summary row. Applied before severity coloring so
    // the capped set's own color spread isn't skewed by tables that got dropped.
    let overflowRowLabel = null;
    let overflowRowFindings = 0;
    {
      const distinctTables = [...new Set(mergedPoints.map((p) => p.yTable))];
      if (distinctTables.length > SCATTER_TABLE_CAP) {
        const ranked = distinctTables
          .slice()
          .sort((a, b) => (totalByTable[b] || 0) - (totalByTable[a] || 0));
        const keep = new Set(ranked.slice(0, SCATTER_TABLE_CAP));
        const overflowTables = ranked.slice(SCATTER_TABLE_CAP);
        overflowRowFindings = overflowTables.reduce((sum, t) => sum + (totalByTable[t] || 0), 0);
        overflowRowLabel = buildAggregateRowLabel(overflowTables.length, dataLabel);
        mergedPoints = mergedPoints.filter((p) => keep.has(p.yTable));
      }
    }

    // Severity color per mark: linear quartile of the view's biggest count —
    // blue → yellow → orange → red (SEVERITY_RAMP). Computed before the name
    // filter so searching doesn't reshuffle colors under the user.
    const maxCount = mergedPoints.reduce((m, p) => Math.max(m, p.count), 0) || 1;
    mergedPoints.forEach((p) => {
      const bin = Math.min(
        SEVERITY_RAMP.length - 1,
        Math.floor((p.count / maxCount) * SEVERITY_RAMP.length)
      );
      p.color = SEVERITY_RAMP[bin];
    });

    let tableNames = [...new Set(mergedPoints.map((p) => p.yTable))];

    // Client-side name filter — matches the live-filter behavior of the masking
    // results search. Empty term keeps everything. Row keys are schema-qualified
    // (`schema.table`), so typing a schema name surfaces all of that schema's tables.
    const term = (filterTerm || '').trim().toLowerCase();
    if (term) {
      tableNames = tableNames.filter((name) => (name || '').toLowerCase().includes(term));
    }

    // Re-sortable Y-axis ordering. The scatter plot renders bottom-to-top, so the
    // last name in this array appears at the top. For a count-descending view the
    // biggest table should read at the top, so we invert the array direction
    // relative to the comparator (asc comparator -> biggest ends up on top).
    if (sortBy === 'name') {
      tableNames.sort((a, b) => (a || '').localeCompare(b || ''));
      if (sortDir === 'asc') tableNames.reverse();
    } else {
      tableNames.sort((a, b) => (totalByTable[a] || 0) - (totalByTable[b] || 0));
      if (sortDir === 'desc') tableNames.reverse();
    }

    // Overflow summary row always sits at the least-prominent end (position 1 — the
    // bottom of the plot, per the "last name = top" rule above) regardless of the
    // active sort, so it never crowds out a real table at the top.
    if (overflowRowLabel) {
      tableNames.unshift(overflowRowLabel);
    }

    const yWordsTickValues = {};
    const yTickPositionsValues = [];
    const yTickWordsValues = tableNames;
    for (let i = 0; i < yTickWordsValues.length; i++) {
      yWordsTickValues[yTickWordsValues[i]] = i + 1;
      yTickPositionsValues.push(i + 1);
    }

    const dominantTypes = [...new Set(mergedPoints.map((p) => p.dominantEntity))];
    const seen = new Set(dominantTypes);
    const entityTypeOrder = [];
    for (let i = 0; i < mostImportantTypes.length; i++) {
      const type = mostImportantTypes[i];
      if (seen.has(type)) entityTypeOrder.push(type);
    }
    const remainder = dominantTypes.filter(
      (t) => !entityTypeOrder.includes(t) && !leastImportantTypes.includes(t)
    );
    remainder.sort((a, b) => a.localeCompare(b));
    entityTypeOrder.push(...remainder);
    for (let i = 0; i < leastImportantTypes.length; i++) {
      const type = leastImportantTypes[i];
      if (seen.has(type)) entityTypeOrder.push(type);
    }

    const newWordsTickValues = {};
    for (let x = 0; x < entityTypeOrder.length; x++) {
      newWordsTickValues[entityTypeOrder[x]] = x + 1;
    }

    const xTickPositionsValues = entityTypeOrder.map((_, i) => i + 1);
    const xTickWordsValues = entityTypeOrder.map((s) => uiLabel(s));

    const dataValues = mergedPoints
      // Drop points whose table was removed by the filter — otherwise they'd
      // collapse onto y=1 via the fallback and plot on the wrong row.
      .filter((p) => yWordsTickValues[p.yTable] != null)
      .map((p) => {
        const xBase = newWordsTickValues[p.dominantEntity];
        return {
          ...p,
          xBase,
          x: xBase,
          y: yWordsTickValues[p.yTable]
        };
      });

    let heightValue = 300;
    const marginValues = {
      left: 150,
      right: 0,
      bottom: 0,
      top: 75
    };

    // File rows render a 3-line tick (name + two-line folder path), so they get a
    // wider row pitch than the 2-line table ticks.
    const rowPitch = dataLabel === 'files' ? 50 : 40;
    for (let prop in yWordsTickValues) {
      heightValue += rowPitch;
    }

    const dominantSumByTable = {};
    mergedPoints.forEach((p) => {
      dominantSumByTable[p.table] = (dominantSumByTable[p.table] || 0) + p.count;
    });

    const tableRowFindingsLabel = {};
    tables.forEach((t) => {
      const name = overviewTableKey(t);
      if (entity === 'ALL') {
        tableRowFindingsLabel[name] = dominantSumByTable[name] || 0;
      } else {
        let s = 0;
        (t.fields || []).forEach((f) => {
          const c = (f.findings || {})[entity];
          if (c) s += c;
        });
        tableRowFindingsLabel[name] = s;
      }
    });
    if (overflowRowLabel) {
      tableRowFindingsLabel[overflowRowLabel] = overflowRowFindings;
    }

    return {
      marginValues,
      heightValue,
      xTickPositionsValues,
      xTickWordsValues,
      yTickPositionsValues,
      yTickWordsValues,
      dataValues,
      tableTotalCounts: (entityTablesForTotals && entityTablesForTotals.TOTAL) || totalByTable,
      tableRowFindingsLabel
    };
  } catch (e) {
    console.log(e);
    return {
      marginValues: { left: 150, right: 0, bottom: 0, top: 75 },
      heightValue: 300,
      xTickPositionsValues: [],
      xTickWordsValues: [],
      yTickPositionsValues: [],
      yTickWordsValues: [],
      dataValues: [],
      tableTotalCounts: {},
      tableRowFindingsLabel: {}
    };
  }
};


export { getScatterVisData };